import { AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import client from '../../src/api/client';

const PENDING_ID_CHECKOUT_KEY = 'alu_pending_id_checkout';

const resolveLabel = (paymentFor) => {
  if (paymentFor === 'id_card_request') return 'ID Card Request';
  if (paymentFor === 'venue_booking') return 'Venue Booking';
  return 'Payment';
};

export default function PaymentCancelPage() {
  const [searchParams] = useSearchParams();
  const paymentFor = String(searchParams.get('paymentFor') || '').toLowerCase();
  const referenceId = searchParams.get('referenceId');
  const sessionId = searchParams.get('session_id');
  const label = resolveLabel(paymentFor);
  const [cancelState, setCancelState] = useState({ status: 'idle', message: '' });

  const numericReferenceId = useMemo(() => {
    const parsed = Number(referenceId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [referenceId]);

  useEffect(() => {
    let isMounted = true;

    if (!sessionId) {
      setCancelState({ status: 'idle', message: 'No checkout session was provided in the URL.' });
      return () => {
        isMounted = false;
      };
    }

    const cancelSession = async () => {
      setCancelState({ status: 'loading', message: 'Cancelling your pending checkout...' });

      try {
        await client.post('/api/payments/cancel-session', {
          sessionId,
          paymentFor,
          referenceId: numericReferenceId,
        });

        if (!isMounted) return;
        setCancelState({ status: 'success', message: 'Your pending payment was cancelled immediately.' });
      } catch (error) {
        if (!isMounted) return;
        const backendMessage = String(error?.response?.data?.message || '').trim();
        if (error?.response?.status === 409 && backendMessage) {
          setCancelState({ status: 'success', message: backendMessage });
          return;
        }

        setCancelState({
          status: 'error',
          message: backendMessage || 'Unable to cancel the checkout immediately. Stripe expiry fallback will still apply.',
        });
      } finally {
        try {
          sessionStorage.removeItem(PENDING_ID_CHECKOUT_KEY);
        } catch {
          // Ignore storage access errors.
        }
      }
    };

    cancelSession();

    return () => {
      isMounted = false;
    };
  }, [numericReferenceId, paymentFor, sessionId]);

  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '2rem', background: '#fff7ed' }}>
      <section style={{ width: '100%', maxWidth: 680, background: '#fff', borderRadius: 16, padding: '2rem', boxShadow: '0 12px 30px rgba(124, 45, 18, 0.10)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#c2410c', marginBottom: '1rem' }}>
          <AlertTriangle size={28} />
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Payment Not Completed</h1>
        </div>

        <p style={{ marginTop: 0, color: '#7c2d12' }}>
          Your {label.toLowerCase()} payment was canceled. No charge was made.
        </p>

        {referenceId ? (
          <p style={{ color: '#7c2d12', marginBottom: '1rem' }}>
            <strong>Reference ID:</strong> {referenceId}
          </p>
        ) : null}

        {sessionId ? (
          <p style={{ color: '#7c2d12', marginBottom: '1rem', wordBreak: 'break-all' }}>
            <strong>Stripe Session:</strong> {sessionId}
          </p>
        ) : null}

        <div
          style={{
            marginBottom: '1rem',
            borderRadius: 10,
            padding: '0.75rem 0.9rem',
            border: '1px solid #fed7aa',
            background: cancelState.status === 'error' ? '#fff1f2' : '#fff7ed',
            color: cancelState.status === 'error' ? '#9f1239' : '#7c2d12',
          }}
        >
          {cancelState.status === 'loading' ? 'Cancelling your checkout now...' : cancelState.message}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Link
            to={paymentFor === 'venue_booking' ? '/venues' : '/digital-id'}
            style={{ background: '#c2410c', color: '#fff', textDecoration: 'none', padding: '0.65rem 1rem', borderRadius: 10, fontWeight: 600 }}
          >
            Try Payment Again
          </Link>
          <Link to="/dashboard" style={{ background: '#fff', color: '#7c2d12', textDecoration: 'none', padding: '0.65rem 1rem', borderRadius: 10, fontWeight: 600, border: '1px solid #fdba74' }}>
            Back to Dashboard
          </Link>
        </div>
      </section>
    </main>
  );
}
